/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Sets announcement height CSS variable for sticky header positioning
 */
function setAnnouncementHeight() {
  const announcement = document.querySelector('.cc-announcement');
  if (announcement) {
    const height = announcement.offsetHeight;
    document.documentElement.style.setProperty('--announcement-height', `${height}px`);
  } else {
    document.documentElement.style.setProperty('--announcement-height', '0px');
  }
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      this.isPaused = false;
      this.currentSlide = 0;
      this.slides = [];
      this.autoSlideTimeout = null;
      this.touchStartX = null;
      this.touchStartY = null;
      this.touchEndX = null;
      this.touchEndY = null;

      this.bindEvents();
      this.init();
    }

    disconnectedCallback() {
      // Clear auto-slide timeout
      if (this.autoSlideTimeout) {
        clearTimeout(this.autoSlideTimeout);
      }

      // Remove event listeners
      if (this.moveLinksHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.moveLocalizationHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      // Remove touch event listeners
      if (this.slider) {
        this.slider.removeEventListener('touchstart', this.handleTouchStart);
        this.slider.removeEventListener('touchmove', this.handleTouchMove);
        this.slider.removeEventListener('touchend', this.handleTouchEnd);
      }
    }

    bindEvents() {
      if (this.links) {
        this.moveLinksHandler = this.moveLinksHandler || this.moveLinks.bind(this);
        window.addEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.localization) {
        this.moveLocalizationHandler = this.moveLocalizationHandler
          || debounce(this.moveLocalization.bind(this));
        window.addEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.slider) {
        this.slider.addEventListener('mouseenter', this.pauseSlider.bind(this));
        this.slider.addEventListener('mouseleave', this.resumeSlider.bind(this));
        
        // Add touch support for mobile
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
        
        this.slider.addEventListener('touchstart', this.handleTouchStart, { passive: true });
        this.slider.addEventListener('touchmove', this.handleTouchMove, { passive: true });
        this.slider.addEventListener('touchend', this.handleTouchEnd, { passive: true });
      }

      // Arrow navigation
      const prevArrow = this.querySelector('.announcement__arrow--prev');
      const nextArrow = this.querySelector('.announcement__arrow--next');
      
      if (prevArrow) {
        prevArrow.addEventListener('click', this.goToPreviousSlide.bind(this));
      }
      
      if (nextArrow) {
        nextArrow.addEventListener('click', this.goToNextSlide.bind(this));
      }
    }

    /**
     * Handle touch start for swipe detection
     */
    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.pauseSlider();
    }

    /**
     * Handle touch move for swipe detection
     */
    handleTouchMove(e) {
      if (!this.touchStartX || !this.touchStartY) return;
      
      this.touchEndX = e.touches[0].clientX;
      this.touchEndY = e.touches[0].clientY;
    }

    /**
     * Handle touch end for swipe detection
     */
    handleTouchEnd() {
      if (!this.touchStartX || !this.touchEndX) {
        this.resumeSlider();
        return;
      }
      
      const diffX = this.touchStartX - this.touchEndX;
      const diffY = Math.abs(this.touchStartY - this.touchEndY);
      
      // Only trigger if horizontal swipe is more significant than vertical
      if (Math.abs(diffX) > 50 && diffY < 100) {
        if (diffX > 0) {
          // Swiped left, go to next slide
          this.goToNextSlide();
        } else {
          // Swiped right, go to previous slide
          this.goToPreviousSlide();
        }
      }
      
      // Reset values
      this.touchStartX = null;
      this.touchStartY = null;
      this.touchEndX = null;
      this.touchEndY = null;
      
      this.resumeSlider();
    }

    /**
     * Start sliders a slidin'
     */
    init() {
      if (this.slider) {
        this.slides = this.slider.querySelectorAll('.announcement__text');
        if (this.slides.length > 0) {
          this.slides[0].classList.add('is-visible');
          
          // Start auto-slide only if there are multiple slides
          if (this.slides.length > 1) {
            this.startAutoSlide();
          }
        }
      }

      if (this.links) {
        this.moveLinks();
      }

      if (this.localization) {
        this.moveLocalization();
      }

      // Set announcement height for sticky header positioning
      setTimeout(() => setAnnouncementHeight(), 100);
      
      // Set it again after a delay to ensure proper calculation on mobile
      setTimeout(() => setAnnouncementHeight(), 500);
    }

    /**
     * Start auto-sliding
     */
    startAutoSlide() {
      if (this.autoSlideTimeout) {
        clearTimeout(this.autoSlideTimeout);
      }
      
      // Skip if there's only one slide
      if (this.slides.length <= 1) return;
      
      this.autoSlideTimeout = setTimeout(() => {
        if (!this.isPaused) {
          // Check if element is in viewport, or if it's at the top of the page (announcement bar)
          const isVisible = theme.elementUtil && theme.elementUtil.isInViewport 
            ? theme.elementUtil.isInViewport(this) 
            : true;
          
          // For announcement bars at the top, always animate (they're always "visible")
          const rect = this.getBoundingClientRect();
          const isAtTop = rect.top >= -50 && rect.top <= 150; // Within 150px of viewport top
          
          if (isVisible || isAtTop) {
            this.goToNextSlide();
          }
        }
        this.startAutoSlide();
      }, this.dataset.slideDelay);
    }

    /**
     * Go to next slide
     */
    goToNextSlide() {
      if (this.slides.length <= 1) return;
      
      const nextSlideIndex = (this.currentSlide + 1) % this.slides.length;
      this.goToSlide(nextSlideIndex);
    }

    /**
     * Go to previous slide
     */
    goToPreviousSlide() {
      if (this.slides.length <= 1) return;
      
      const prevSlideIndex = this.currentSlide === 0 ? this.slides.length - 1 : this.currentSlide - 1;
      this.goToSlide(prevSlideIndex);
    }

    /**
     * Go to specific slide
     */
    goToSlide(slideIndex) {
      if (slideIndex === this.currentSlide || slideIndex < 0 || slideIndex >= this.slides.length) return;
      
      // Update current slide
      this.slides[this.currentSlide].classList.remove('is-visible');
      this.slides[slideIndex].classList.add('is-visible');
      this.currentSlide = slideIndex;

      // Scroll to slide with better mobile compatibility
      const targetScroll = document.dir === 'rtl' 
        ? -Math.abs(slideIndex * this.slides[slideIndex].clientWidth)
        : this.slides[slideIndex].offsetLeft;
      
      // Use smooth scroll with fallback for better mobile support
      if ('scrollTo' in this.slider) {
        this.slider.scrollTo({
          left: targetScroll,
          behavior: 'smooth'
        });
      } else {
        // Fallback for older browsers
        this.slider.scrollLeft = targetScroll;
      }

      // Update announcement height in case slide height differs
      setTimeout(() => setAnnouncementHeight(), 50);

      // Restart auto-slide timer
      this.startAutoSlide();
    }

    /**
     * Move the help links selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLinks() {
      const menuAnnouncementLinks = document.querySelector('.mob__announcement-links');
      if (!theme.mediaMatches.md && !menuAnnouncementLinks) {
        // Move localization to mobile
        const mobNav = document.createElement('nav');
        mobNav.classList.add('mob__announcement-links');

        const mobNavUl = document.createElement('ul');
        mobNav.classList.add('secondary-nav');

        this.links.forEach((link) => {
          mobNav.innerHTML += `<li><a class="secondary-nav__item" href="${link.href}">${link.innerText}</a>`;
        });

        mobNav.appendChild(mobNavUl);
        this.menu.appendChild(mobNav);
      } else if (theme.mediaMatches.md && menuAnnouncementLinks) {
        menuAnnouncementLinks.remove();
      }
    }

    /**
     * Move the localization selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLocalization() {
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      const menuLocalization = document.querySelector('.mob__localization');
      if (!theme.mediaMatches.md && !menuLocalization) {
        // Move localization to mobile
        const mobLocalizationElem = document.createElement('div');
        mobLocalizationElem.classList.add('mob__localization');
        mobLocalizationElem.appendChild(this.localization.firstElementChild);
        this.menu.appendChild(mobLocalizationElem);
      } else if (theme.mediaMatches.md && menuLocalization) {
        // Move localization back to announcement bar
        this.localization.appendChild(menuLocalization.firstElementChild);
        menuLocalization.remove();
      }
    }

    /**
     * Pauses the auto-rotating slider
     */
    pauseSlider() {
      this.isPaused = true;
    }

    /**
     * Resumes the auto-rotating slider
     */
    resumeSlider() {
      this.isPaused = false;
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}

// Set announcement height when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  setTimeout(setAnnouncementHeight, 200);
});

// Also set it on window load as a fallback
window.addEventListener('load', () => {
  setTimeout(setAnnouncementHeight, 100);
});
